import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Highcharts from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import { ASSAM_GEOJSON, HC_NAME_MAP } from '../../data/mainDashboardMockData';
import { fetchDistrictAvailability } from '../../data/api';
import DistrictStoreModal from '../DistrictStoreModal';

const TAB_LABELS = {
  all: 'All Facilities', ddwh: 'DDWH', mc: 'Medical Colleges',
  dh: 'District Hospitals', sdh: 'SDH', chc: 'CHC/MH', phc: 'PHC', other: 'Other Facilities'
};

const VIEW_MODES = [
  { id: 'avail', label: 'Stock Availability', icon: 'fas fa-chart-bar' },
  { id: 'stockout', label: 'Stockout Analysis', icon: 'fas fa-exclamation-triangle' },
];

const AssamMapFull = () => {
  const [currentFacType, setCurrentFacType] = useState('all');
  const [viewMode, setViewMode] = useState('avail');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const wrapperRef = useRef(null);

  // Re-fetch data whenever facility type changes
  useEffect(() => {
    setLoading(true);
    fetchDistrictAvailability(currentFacType === 'all' ? '' : currentFacType)
      .then(r => { setDistricts(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [currentFacType]);

  useEffect(() => {
    const handleResize = () => { if (chartRef.current) chartRef.current.reflow(); };
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(handleResize, 300);
    return () => { window.removeEventListener('resize', handleResize); clearTimeout(timer); };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => { if (chartRef.current) chartRef.current.reflow(); }, 150);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const getColor = useCallback((val, so) => {
    if (viewMode === 'stockout') {
      const soVal = parseInt(so) || 0;
      if (soVal === 0) return '#27ae60';
      if (soVal <= 1) return '#a7f3d0';
      if (soVal <= 3) return '#fde68a';
      if (soVal <= 5) return '#f39c12';
      return '#e74c3c';
    }
    if (val >= 90) return '#27ae60';
    if (val >= 75) return '#f39c12';
    return '#e74c3c';
  }, [viewMode]);

  const mapData = useMemo(() => {
    if (!districts.length) return [];
    // Group by GeoJSON name to aggregate districts that map to the same polygon
    const grouped = {};
    districts.forEach(d => {
      const val = parseFloat(d.avail);
      const districtForMap = (d.district || d.n || '').trim();
      const hcName = HC_NAME_MAP[districtForMap] || districtForMap;
      if (!grouped[hcName]) {
        grouped[hcName] = {
          name: hcName,
          labels: [],
          apiNames: [],
          facilities: [],
          totalStores: 0, storesAbove90: 0, stockoutStores: 0,
          availSum: 0, count: 0
        };
      }
      const g = grouped[hcName];
      g.labels.push(d.n || districtForMap);
      g.apiNames.push(districtForMap);
      if (d.facility) g.facilities.push(d.facility);
      g.totalStores += parseInt(d.total_stores) || 0;
      g.storesAbove90 += parseInt(d.stores_above_90) || 0;
      g.stockoutStores += parseInt(d.stockout_stores) || 0;
      g.availSum += val;
      g.count += 1;
    });
    const hasFacilityNames = ['ddwh','mc','dh','sdh','chc','phc'].includes(currentFacType);
    return Object.values(grouped).map(g => {
      const avgAvail = g.count > 0 ? g.availSum / g.count : 0;
      // Map label: district name with count for facility tabs
      let label;
      if (hasFacilityNames) {
        label = g.apiNames[0] + (g.count > 1 ? ' (' + g.count + ')' : '');
      } else {
        label = g.apiNames[0] || g.name;
      }
      return {
        name: g.name,
        value: viewMode === 'stockout' ? g.stockoutStores : parseFloat(avgAvail.toFixed(2)),
        color: getColor(avgAvail, g.stockoutStores),
        districtLabel: label,
        districtApiName: g.apiNames[0],
        facilityName: hasFacilityNames ? g.labels.join(', ') : null,
        avail: parseFloat(avgAvail.toFixed(2)),
        totalStores: g.totalStores,
        storesAbove90: g.storesAbove90,
        stockoutStores: g.stockoutStores
      };
    });
  }, [districts, viewMode, getColor]);

  // Summary stats
  const stats = useMemo(() => {
    if (!districts.length) return {};
    const total = districts.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
    const avgAvail = (districts.reduce((s, d) => s + parseFloat(d.avail), 0) / districts.length).toFixed(1);
    const good = districts.filter(d => parseFloat(d.avail) >= 90).length;
    const warning = districts.filter(d => parseFloat(d.avail) >= 75 && parseFloat(d.avail) < 90).length;
    const critical = districts.filter(d => parseFloat(d.avail) < 75).length;
    const totalStockout = districts.reduce((s, d) => s + (parseInt(d.stockout_stores) || 0), 0);
    return { total, avgAvail, good, warning, critical, totalStockout, districtCount: districts.length };
  }, [districts]);

  const mapOptions = useMemo(() => ({
    chart: {
      map: ASSAM_GEOJSON,
      backgroundColor: 'transparent',
      margin: [10, 10, 10, 10],
      spacing: [10, 10, 10, 10],
      animation: { duration: 400 },
      reflow: true
    },
    title: { text: '' },
    credits: { enabled: false },
    mapNavigation: { enabled: true, buttonOptions: { verticalAlign: 'bottom' } },
    colorAxis: { visible: false, min: 0, max: 100 },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      formatter: function () {
        var p = this.point;
        var facLabel = TAB_LABELS[currentFacType] || 'All Facilities';
        var modeLabel = viewMode === 'stockout' ? 'Stockout Analysis' : 'Stock Availability';
        var isItemLevel = ['ddwh','mc','dh','sdh','chc','phc'].indexOf(currentFacType) !== -1;
        // Show facility names as a list
        var facNames = p.facilityName ? p.facilityName.split(', ') : [];
        var facHtml = '';
        if (facNames.length > 0 && facNames.length <= 3) {
          facHtml = '<div style="font-size:10px;color:#1e3a5f;margin-bottom:4px;font-weight:600;line-height:1.4">' + facNames.join('<br>') + '</div>';
        } else if (facNames.length > 3) {
          facHtml = '<div style="font-size:10px;color:#1e3a5f;margin-bottom:4px;font-weight:600">' + facNames.length + ' facilities</div>';
        }
        // Always show district/GeoJSON name as title
        var title = p.districtApiName || p.name;
        return '<div style="padding:8px 4px;min-width:200px;max-width:280px">' +
          '<div style="font-weight:700;font-size:13px;margin-bottom:4px;padding-bottom:6px;border-bottom:2px solid #e8ecf1">' +
          title + '</div>' +
          facHtml +
          '<div style="font-size:9px;color:#3b82f6;font-weight:600;margin-bottom:8px;text-transform:uppercase;letter-spacing:.5px">' + facLabel + ' \u2022 ' + modeLabel + '</div>' +
          '<table style="border-collapse:collapse;width:100%">' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Availability</td><td style="font-weight:700;text-align:right;color:' + (p.avail >= 90 ? '#27ae60' : p.avail >= 75 ? '#f39c12' : '#e74c3c') + '">' + p.avail + '%</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">' + (isItemLevel ? 'Total Items' : 'Total Stores') + '</td><td style="font-weight:700;text-align:right">' + (p.totalStores || '0') + '</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">' + (isItemLevel ? 'Items In Stock' : 'Stores \u2265 90%') + '</td><td style="font-weight:700;text-align:right;color:#27ae60">' + (p.storesAbove90 || '0') + '</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">' + (isItemLevel ? 'Items Out of Stock' : 'Stockout Stores') + '</td><td style="font-weight:700;text-align:right;color:#e74c3c">' + (p.stockoutStores || '0') + '</td></tr>' +
          '</table>' +
          '<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e8ecf1;font-size:10px;color:#3b82f6;font-weight:600;text-align:center;cursor:pointer">\u27A4 Click to view details</div>' +
          '</div>';
      }
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              const apiName = this.districtApiName || this.districtLabel || this.name;
              setSelectedDistrict(apiName);
            }
          }
        }
      }
    },
    series: [{
      data: mapData,
      joinBy: ['NAME', 'name'],
      name: 'Stock Availability',
      borderWidth: 2,
      borderColor: '#ffffff',
      nullColor: '#e2e8f0',
      states: { hover: { borderColor: '#1e293b', borderWidth: 3, brightness: 0.08 } },
      dataLabels: {
        enabled: true,
        format: '{point.districtLabel}',
        style: {
          fontSize: '9px', fontWeight: '700', color: '#1e293b',
          textOutline: '2px white', fontFamily: "'Segoe UI', system-ui, sans-serif"
        }
      }
    }]
  }), [mapData, currentFacType, viewMode]);

  const tabs = [
    { id: 'all', label: 'All Facilities', icon: 'fas fa-globe' },
    { id: 'ddwh', label: 'DDWH', icon: 'fas fa-warehouse' },
    { id: 'mc', label: 'Medical Colleges', icon: 'fas fa-university' },
    { id: 'dh', label: 'District Hospitals', icon: 'fas fa-hospital' },
    { id: 'sdh', label: 'SDH', icon: 'fas fa-clinic-medical' },
    { id: 'chc', label: 'CHC/MH', icon: 'fas fa-house-medical' },
    { id: 'phc', label: 'PHC', icon: 'fas fa-briefcase-medical' },
    { id: 'other', label: 'Other Facilities', icon: 'fas fa-building' }
  ];

  return (
    <>
      <div ref={wrapperRef} style={{
        background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden',
        ...(isFullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, borderRadius: 0 } : {})
      }}>

        {/* Top Bar */}
        <div style={{
          padding: '8px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #e0ecff 0%, #eef4ff 100%)'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' }}>
              <i className="fas fa-map-marked-alt" style={{ color: '#3b82f6', marginRight: 10 }}></i>
              District Heat Map
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '.78rem', color: '#64748b' }}>
              Click on any district to view store-level stock availability details
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={toggleFullscreen} style={{
              width: 36, height: 36, borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#1e3a5f', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem',
              transition: 'background 0.2s'
            }} title="Toggle fullscreen">
              <i className={isFullscreen ? 'fas fa-compress' : 'fas fa-expand'}></i>
            </button>
          </div>
        </div>

        {/* Facility Type Tabs */}
        <div style={{
          padding: '10px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto',
          background: '#fafbfc'
        }}>
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setCurrentFacType(tab.id)} style={{
              padding: '8px 18px', borderRadius: 20, fontSize: '.75rem', fontWeight: 600,
              border: currentFacType === tab.id ? '2px solid #1e3a5f' : '1px solid #d1d5db',
              background: currentFacType === tab.id ? '#1e3a5f' : '#fff',
              color: currentFacType === tab.id ? '#fff' : '#374151',
              cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 6
            }}>
              <i className={tab.icon} style={{ fontSize: '.7rem' }}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Bar */}
        {stats.districtCount > 0 && (
          <div style={{
            padding: '10px 24px', borderBottom: '1px solid #f1f5f9',
            display: 'flex', gap: 24, alignItems: 'center', fontSize: '.78rem',
            background: '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 700, color: '#1e293b' }}>
                {TAB_LABELS[currentFacType]}
              </span>
              <span style={{ color: '#94a3b8' }}>|</span>
              <span style={{ color: '#64748b' }}>{stats.districtCount} {['ddwh','mc','dh','sdh','chc','phc'].includes(currentFacType) ? 'facilities' : 'districts'}</span>
              <span style={{ color: '#94a3b8' }}>|</span>
              <span style={{ color: '#64748b' }}>{stats.total} {['ddwh','mc','dh','sdh','chc','phc'].includes(currentFacType) ? 'items' : 'stores'}</span>
              <span style={{ color: '#94a3b8' }}>|</span>
              <span style={{ color: '#64748b' }}>Avg: <b style={{ color: parseFloat(stats.avgAvail) >= 90 ? '#27ae60' : '#f39c12' }}>{stats.avgAvail}%</b></span>
            </div>
            <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#27ae60', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600, color: '#27ae60' }}>{stats.good}</span>
                <span style={{ color: '#94a3b8' }}>Good</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f39c12', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600, color: '#f39c12' }}>{stats.warning}</span>
                <span style={{ color: '#94a3b8' }}>Moderate</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#e74c3c', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600, color: '#e74c3c' }}>{stats.critical}</span>
                <span style={{ color: '#94a3b8' }}>Critical</span>
              </span>
            </div>
          </div>
        )}

        {/* Map Container */}
        <div style={{
          position: 'relative',
          height: isFullscreen ? 'calc(100vh - 200px)' : '600px',
          background: '#fafbfc'
        }}>
          {loading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.8)', zIndex: 5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 10
            }}>
              <div style={{
                width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite'
              }} />
              <span style={{ fontSize: '.85rem', color: '#3b82f6', fontWeight: 600 }}>
                Loading {TAB_LABELS[currentFacType]} data...
              </span>
            </div>
          )}
          {mapData.length > 0 ? (
            <HighchartsReact
              key={`${isFullscreen}-${currentFacType}-${viewMode}`}
              highcharts={Highcharts}
              constructorType={'mapChart'}
              options={mapOptions}
              callback={(chart) => { chartRef.current = chart; }}
              containerProps={{ style: { height: '100%', width: '100%' } }}
            />
          ) : !loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', flexDirection: 'column', gap: 12
            }}>
              <i className="fas fa-map" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 600 }}>
                No availability data for <b>{TAB_LABELS[currentFacType]}</b>
              </span>
              <span style={{ fontSize: '.8rem', color: '#cbd5e1' }}>
                Try selecting a different facility type
              </span>
            </div>
          ) : null}
        </div>

        {/* Bottom Legend */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'center', gap: 32, background: '#fff'
        }}>
          {viewMode === 'avail' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#27ae60', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>&ge;90%</span> <span style={{ color: '#94a3b8' }}>Good</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#f39c12', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>75&ndash;89%</span> <span style={{ color: '#94a3b8' }}>Moderate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#e74c3c', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>&lt;75%</span> <span style={{ color: '#94a3b8' }}>Critical</span>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#27ae60', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>0</span> <span style={{ color: '#94a3b8' }}>No Stockout</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#fde68a', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>1&ndash;3</span> <span style={{ color: '#94a3b8' }}>Low</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#f39c12', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>4&ndash;5</span> <span style={{ color: '#94a3b8' }}>Moderate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem' }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#e74c3c', display: 'inline-block' }}></span>
                <span style={{ fontWeight: 600 }}>&gt;5</span> <span style={{ color: '#94a3b8' }}>High</span>
              </div>
            </>
          )}
        </div>

        {/* District Listing Table — hidden */}
      </div>

      {selectedDistrict && (
        <DistrictStoreModal
          district={selectedDistrict}
          mode={viewMode}
          facType={currentFacType}
          onClose={() => setSelectedDistrict(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      `}</style>
    </>
  );
};

const thS = {
  padding: '10px 14px', fontWeight: 700, fontSize: '.7rem',
  textTransform: 'uppercase', letterSpacing: '.5px', color: '#64748b',
  borderBottom: '2px solid #e2e8f0', textAlign: 'center', whiteSpace: 'nowrap'
};

const tdS = {
  padding: '8px 14px', borderBottom: '1px solid #f1f5f9'
};

export default AssamMapFull;
